import cx from 'classnames';
import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { RevisionLicenseConclusionScope } from '../../../../../../../shared/model-typings/revisionlicenseconclusion';
import { useDeleteLicense } from '../../../../../../hooks/api/correctionsV2/useDeleteLicense';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import Button from '../../../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button/button-constants';
import type { ConclusionData } from '../../../../../../../shared/types/licenses';

import { ConcludeMenuButton } from './ConcludeMenuButton';
import type { LicenseTabScope } from './types';

import styles from './DependencyLicenses.module.less';

type Props = {
  licenseSignature: string;
  revisionLicenseId: number;
  scope: LicenseTabScope;
  isConcluded?: boolean;
  conclusions: ConclusionData;
  packageName: string;
  onView: () => void;
  canEditLicenses: boolean;
  isLicenseConcludedEnabled: boolean;
};

export const LicenseActionButtons: VoidFunctionComponent<Props> = ({
  licenseSignature,
  revisionLicenseId,
  scope,
  isConcluded,
  conclusions,
  packageName,
  onView,
  canEditLicenses,
  isLicenseConcludedEnabled,
}) => {
  const dispatch = useDispatch();
  // A local conclusion is written at the project or release group, but the drawer reads at the revision or release.
  const conclusionScopes = useMemo(() => {
    if (scope.type === 'project') {
      return {
        chooseParams: {
          dependencyRevisionLocator: scope.dependencyRevisionLocator,
          scope: { scope: RevisionLicenseConclusionScope.PROJECT, projectLocator: scope.projectLocator },
          originId: scope.revisionLocator,
        },
        readScope: {
          scope: RevisionLicenseConclusionScope.REVISION,
          projectLocator: scope.projectLocator,
          revisionLocator: scope.revisionLocator,
        },
        localNoun: 'project',
      } as const;
    }

    if (scope.type === 'releaseGroup') {
      return {
        chooseParams: {
          dependencyRevisionLocator: scope.dependencyRevisionLocator,
          scope: { scope: RevisionLicenseConclusionScope.RELEASE_GROUP, releaseGroupId: scope.releaseGroupId },
          originId: scope.releaseId.toString(),
        },
        readScope: {
          scope: RevisionLicenseConclusionScope.RELEASE,
          releaseGroupId: scope.releaseGroupId,
          releaseId: scope.releaseId,
        },
        localNoun: 'release group',
      } as const;
    }

    return null;
  }, [scope]);

  const { mutateAsync: deleteLicense, isPending: isDeletingLicense } = useDeleteLicense({
    dependencyRevisionLocator: scope.dependencyRevisionLocator,
    revisionLicenseId,
  });

  const refetchDependencies = () => {
    dispatch(DependenciesV2Store.actions.preFetchLoading());
    dispatch(DependenciesV2Store.actions.fetchDependencies());
  };

  return (
    <div className={styles.actionButtonsContainer}>
      <Button size={BUTTON_SIZE.SMALL} type={BUTTON_TYPE.SECONDARY} onClick={() => onView()}>
        <span className={styles.actionButtonText}>View</span>
      </Button>
      {scope.type !== 'global' && canEditLicenses && (
        <Button
          size={BUTTON_SIZE.SMALL}
          type={BUTTON_TYPE.SECONDARY}
          isDisabled={!canEditLicenses || isDeletingLicense}
          onClick={async () => {
            await deleteLicense();
            refetchDependencies();
          }}
        >
          <span className={styles.actionButtonText}>Delete</span>
        </Button>
      )}
      {conclusionScopes && canEditLicenses && isLicenseConcludedEnabled && (
        <ConcludeMenuButton
          licenseId={licenseSignature}
          isConcluded={!!isConcluded}
          packageName={packageName}
          conclusions={conclusions}
          chooseParams={conclusionScopes.chooseParams}
          readScope={conclusionScopes.readScope}
          localNoun={conclusionScopes.localNoun}
          size={BUTTON_SIZE.SMALL}
          idleType={BUTTON_TYPE.SECONDARY}
          labelClassName={cx(styles.actionButtonText, !isConcluded && styles.unConcludedActionButtonText)}
          onDone={refetchDependencies}
        />
      )}
    </div>
  );
};
